import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import {
  Badge, EmptyState, PageLoading, TextInput, Toggle,
} from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import type { DispatchReason } from '../../types';

/**
 * Non-delivery reasons.
 *
 * When a delivery fails, the delivery person picks from this list (or
 * writes a note). The admin owns the list — a reason that has already
 * been used on a delivery is hidden rather than deleted, so past records
 * keep their reason.
 */
export default function DispatchReasonsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const isAdmin = can('company_admin', 'platform_admin');

  const [reasons, setReasons] = useState<DispatchReason[] | null>(null);
  const [editing, setEditing] = useState<DispatchReason | 'new' | null>(null);

  const load = useCallback(async () => {
    try {
      setReasons(await api.get<DispatchReason[]>('/dispatch/reasons'));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load reasons.');
      setReasons([]);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  async function setActive(r: DispatchReason, isActive: boolean) {
    try {
      await api.patch(`/dispatch/reasons/${r.id}`, { isActive });
      setReasons((list) => (list ?? []).map((x) => (x.id === r.id ? { ...x, isActive } : x)));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not update that.');
    }
  }

  return (
    <>
      <PageHeader
        title="Non-delivery reasons"
        subtitle="What the delivery person picks from when a drop can't be completed."
        actions={isAdmin ? (
          <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon name="plus" size={16} /> Add reason
          </button>
        ) : undefined}
      />

      <div className="page col gap-16">
        {reasons === null ? <PageLoading /> : reasons.length === 0 ? (
          <div className="card">
            <EmptyState
              icon="list"
              title="No reasons yet"
              body={isAdmin
                ? 'Add a few, like “Customer not available” or “Shop closed”.'
                : undefined}
            />
          </div>
        ) : (
          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Reason</th>
                    <th className="num">Used</th>
                    <th>Shown to delivery</th>
                    {isAdmin ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {reasons.map((r) => (
                    <tr key={r.id}>
                      <td className="primary">
                        <span className="row gap-8" style={{ alignItems: 'center' }}>
                          {r.label}
                          {!r.isActive ? <Badge tone="neutral">Hidden</Badge> : null}
                        </span>
                      </td>
                      <td className="num">{r.useCount ?? 0}</td>
                      <td>
                        {isAdmin
                          ? <Toggle checked={r.isActive} label="" onChange={(v) => void setActive(r, v)} />
                          : (r.isActive ? 'Yes' : 'No')}
                      </td>
                      {isAdmin ? (
                        <td>
                          <button type="button" className="icon-btn" title="Edit"
                            onClick={() => setEditing(r)}>
                            <Icon name="edit" size={15} />
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {editing ? (
        <ReasonDialog
          reason={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      ) : null}
    </>
  );
}

function ReasonDialog({ reason, onClose, onSaved }: {
  reason: DispatchReason | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState(reason?.label ?? '');

  async function save() {
    setBusy(true);
    try {
      if (reason) await api.patch(`/dispatch/reasons/${reason.id}`, { label: label.trim() });
      else await api.post('/dispatch/reasons', { label: label.trim() });
      toast.success(reason ? 'Saved.' : `${label.trim()} added.`);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!reason) return;
    setBusy(true);
    try {
      const r = await api.del<{ deactivated: boolean; stopsUsing?: number }>(`/dispatch/reasons/${reason.id}`);
      toast.success(r.deactivated
        ? `${reason.label} has been used on ${r.stopsUsing} delivery(s), so it was hidden rather than deleted.`
        : `${reason.label} removed.`);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not remove that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={reason ? reason.label : 'Add a reason'}
      onClose={onClose}
      footer={(
        <>
          {reason ? (
            <button type="button" className="btn btn-danger-ghost" disabled={busy} onClick={() => void remove()}>
              Remove
            </button>
          ) : null}
          <span className="grow" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={busy || !label.trim()}
            onClick={() => void save()}>
            {reason ? 'Save' : 'Add'}
          </button>
        </>
      )}
    >
      <TextInput
        label="Reason"
        value={label}
        placeholder="Customer not available"
        onChange={(e) => setLabel(e.target.value)}
        autoFocus
      />
    </Modal>
  );
}
